import { defineStore } from 'pinia'
import { appMode } from '@/utils/config/mode'
import { makeResetStoreAction } from '@/utils/helpers/pinia'

const isStagingDevOrEEnv = () => appMode.isEEnv || appMode.isStagingOrDev || appMode.isTest

/**
 * Determines the initial state of developer things visibility
 * @returns {boolean} Whether developer things should be shown initially
 */
export const initialShowDevThings = () => isStagingDevOrEEnv()
  ? JSON.parse(localStorage?.[LSK.DEV_SHOW_DEV_THINGS] ?? 'false')
  : false

export const initialShowFeature = (key) => {
  if (!isStagingDevOrEEnv()) return false

  const showFeatureLS = JSON.parse(localStorage?.[LSK.DEV_SHOW_FEATURE] ?? '{}')
  const defaultValue = isStagingDevOrEEnv() || false

  return {
    dummy: defaultValue,
    costEstimation: defaultValue,
    cloudEnvironments: defaultValue,
    groupResources: defaultValue,
    multipleEmails: defaultValue,
    notifications: defaultValue,
    sso: defaultValue,
    whiteLabel: defaultValue,
    ...showFeatureLS,
  }[key] ?? false
}

export const state = () => ({
  betaFeatures: ['whiteLabel', 'sso', 'cloudEnvironments'],
  showDevLayer: false,
  showDevThings: initialShowDevThings(),
  showDevFeature: {
    dummy: initialShowFeature('dummy'),
    costEstimation: initialShowFeature('costEstimation'),
    cloudEnvironments: true,
    groupResources: initialShowFeature('groupResources'),
    multipleEmails: initialShowFeature('multipleEmails'),
    notifications: initialShowFeature('notifications'),
    sso: true,
    whiteLabel: true,
  },
  mockedEndpointsCalled: [],
  permissionsChecked: [],
})

export const useDevStore = defineStore('dev', {
  state,

  getters: {
    hasMockedEndpoints: (state) => !_.isEmpty(state.mockedEndpointsCalled),

    permissionsCount: (state) =>
      state.permissionsChecked.reduce((acc, { action }) => {
        acc[action] = _.get(acc, action, 0) + 1
        return acc
      }, {}),

    $isBetaFeature: (state) =>
      _(state.showDevFeature)
        .omit('dummy')
        .reduce((result, _val, key) => {
          result[key] = state.betaFeatures.includes(key)
          return result
        }, {}),

    $showDevThings: (state) => state.showDevThings,

    $showFeature: (state) =>
      _(state.showDevFeature)
        .omit('dummy')
        .reduce((result, value, key) => {
          result[key] = state.betaFeatures.includes(key) || _.every([state.showDevThings, value])
          return result
        }, {}),

    hasTooManyPermCalls () {
      return _.values(this.permissionsCount).some((nb) => nb > 6)
    },
  },

  actions: {
    /**
     * Toggles the visibility of developer buttons
     * Only works in staging/development environments
     * Persists the state in localStorage
     * @returns {void}
     */
    TOGGLE_DEV_BTNS () {
      if (!isStagingDevOrEEnv()) return
      this.showDevThings = !this.showDevThings
      localStorage.setItem(LSK.DEV_SHOW_DEV_THINGS, this.showDevThings)
    },

    /**
     * Toggles the developer layer visibility
     * @param {boolean} newValue - The new visibility state. Defaults to opposite of current state
     * @returns {void}
     */
    TOGGLE_DEV_LAYER (newValue = !this.showDevLayer) {
      if (!isStagingDevOrEEnv()) return
      this.showDevLayer = newValue
    },

    /**
     * Toggles a specific developer feature
     * Only works in staging/development environments
     * Persists the state in localStorage
     * @param {Object} params - The parameters object
     * @param {string} params.key - The feature key to toggle
     * @param {boolean} params.value - The new feature state
     * @returns {void}
     */
    TOGGLE_DEV_FEATURE ({ key, value }) {
      if (!isStagingDevOrEEnv() || _.isNil(value)) return
      this.showDevFeature[key] = value
      localStorage.setItem(LSK.DEV_SHOW_FEATURE, JSON.stringify(this.showDevFeature))
    },

    /**
     * Adds a mocked endpoint to the list of called endpoints
     * Ensures uniqueness of the endpoints list
     * @param {string} endpointName - The name of the mocked endpoint
     * @returns {void}
     */
    ADD_MOCKED_ENDPOINT (endpointName) {
      this.mockedEndpointsCalled = _.uniq([
        ...this.mockedEndpointsCalled,
        endpointName,
      ])
    },

    /**
     * Adds a fetched permission to the permissions check list
     * @param {Object} params - The parameters object
     * @param {string} params.action - The permission action
     * @param {string} params.canonical - The canonical form of the permission
     * @returns {void}
     */
    ADD_PERMISSION_FETCHED ({ action, canonical }) {
      this.permissionsChecked.push({ action, canonical })
    },

    /**
     * Resets the development state
    */
    RESET_DEV_STATE: makeResetStoreAction(state),
  },
})
